import * as React from 'react';
import moment from 'moment';
import Button from '@app/react-common/components/button/Button';
import { DATE_RANGE_PRESETS, DateRangePreset } from '@app/storybook/components/datepicker/date-range-presets';
import { CalendarDay, DateRangeApplyEvent, useDateRangePicker } from './useDateRangePicker';

export interface PdqDateRangePickerProps {
  background?: 'default' | 'transparent' | 'light';
  positionRight?: boolean;
  minDate?: string | Date;
  defaultPreset?: DateRangePreset;
  onApply?: (dateRange: DateRangeApplyEvent) => void;
}

const WEEKDAY_INITIALS = moment.weekdaysMin().map((weekday) => weekday.charAt(0));

function getDayClassName(day: CalendarDay): string {
  return [
    'calendar-date',
    day.available ? '' : 'is-disabled',
    day.isStart || day.isEnd ? 'is-active' : '',
    day.isSpanStart ? 'calendar-range-start' : '',
    day.isSpanEnd ? 'calendar-range-end' : '',
    day.inRange ? 'calendar-range' : '',
  ]
    .filter(Boolean)
    .join(' ');
}

function PdqDateRangePickerComponent({
  background = 'default',
  positionRight = false,
  minDate,
  defaultPreset,
  onApply,
}: PdqDateRangePickerProps) {
  const picker = useDateRangePicker({ minDate, defaultPreset, onApply });

  return (
    <div ref={picker.containerRef} className="pdq-date-range-picker-react">
      <input
        id="range-input"
        type="tel"
        value={picker.appliedValue}
        onClick={picker.toggleCalendar}
        className={background}
        readOnly
      />
      {picker.isOpen && (
        <div className={`datepicker-container ${positionRight ? 'right-range' : ''}`}>
          <div className="calendar-wrapper">
            <div className="calendar">
              <div className="calendar-nav">
                <Button
                  className="nav-button prev plain"
                  size="small"
                  variant="light"
                  icon="side_left_arrow"
                  ariaLabel="Previous"
                  onClick={() => picker.changeNav(-1)}
                />
                {picker.isYearMode ? (
                  <div className="nav-title month-title">{picker.yearPageTitle}</div>
                ) : (
                  <div className="nav-title month-title">
                    <span>{picker.navDate.format('MMMM')}</span>
                    <Button
                      className="nav-year plain"
                      size="small"
                      variant="light"
                      text={picker.navDate.format('YYYY')}
                      onClick={picker.openYearPicker}
                    />
                  </div>
                )}
                <Button
                  className="nav-button next plain"
                  size="small"
                  variant="light"
                  icon="side_right_arrow"
                  ariaLabel="Next"
                  onClick={() => picker.changeNav(1)}
                />
              </div>
              {picker.isYearMode ? (
                <div className="year-grid">
                  {picker.yearPage.map((year) => (
                    <button
                      key={year}
                      type="button"
                      className={`year-item ${year === picker.navDate.year() ? 'is-active' : ''}`}
                      disabled={!picker.isYearAvailable(year)}
                      onClick={() => picker.selectYear(year)}>
                      {year}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="calendar-container">
                  <div className="calendar-header">
                    {WEEKDAY_INITIALS.map((initial, index) => (
                      <div key={`${initial}-${index}`} className="calendar-date">
                        {initial}
                      </div>
                    ))}
                  </div>
                  <div className="calendar-body">
                    {picker.days.map((day) => (
                      <div key={day.key} className={getDayClassName(day)}>
                        <button className="date-item" onClick={() => picker.selectDay(day)}>
                          {day.date.date()}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="calendar">
              <div className="calendar-nav">
                <div className="nav-title">Presets</div>
                <ul className="ranges">
                  {DATE_RANGE_PRESETS.map((preset) => (
                    <li
                      key={preset.text}
                      className={picker.activePreset?.offset === preset.offset ? 'marked' : ''}
                      onClick={() => picker.selectPreset(preset)}>
                      {preset.text}
                    </li>
                  ))}
                </ul>
                <Button
                  className="presets-reset plain underline-on-hover"
                  size="xsmall"
                  variant="light"
                  text="Reset"
                  icon="sync"
                  onClick={picker.resetToDefault}
                />
              </div>
            </div>
          </div>
          <div className="calendar-footer">
            <p className="range">{picker.displayValue}</p>
            <Button size="medium" variant="light" text="Cancel" onClick={picker.closeCalendar} />
            <Button size="medium" variant="primary" text="Apply" onClick={picker.applyDateRange} />
          </div>
        </div>
      )}
    </div>
  );
}

export const PdqDateRangePicker = React.memo(PdqDateRangePickerComponent);
